/**
 * Composable booking domain (domestic, international, kalogistics).
 *
 * staleTime: 30 detik (list), 5 menit (detail).
 * Origins/destinations kalogistics di-cache 1 jam (data referensi).
 */
import { computed, unref } from "@vue/composition-api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { keys } from "@/queries/keys";
import {
  fetchBookingDomestic,
  fetchBookingDomesticById,
  createBookingDomestic,
  updateBookingDomestic,
  deleteBookingDomestic,
  fetchBookingInternational,
  fetchBookingInternationalById,
  createBookingInternational,
  updateBookingInternational,
  deleteBookingInternational,
  fetchBookingKalogistics,
  fetchBookingKalogisticsOrigins,
  fetchBookingKalogisticsDestinations,
  approveBookingKalogistics,
} from "@/queries/api/booking";

const LIST_STALE = 30 * 1000;
const DETAIL_STALE = 5 * 60 * 1000;
const REF_STALE = 60 * 60 * 1000;

function p(input) {
  return unref(input);
}

/* ============================================================
 * Booking Domestic
 * ============================================================ */

export function useBookingDomestic(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.booking.domestic(p(params))),
    queryFn: () => fetchBookingDomestic(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useBookingDomesticDetail(id, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.booking.all, "domestic", "detail", p(id)]),
    queryFn: () => fetchBookingDomesticById(p(id)),
    staleTime: DETAIL_STALE,
    enabled: computed(() => !!p(id)),
    ...options,
  });
}

function invalidateBookingDomestic(qc) {
  return qc.invalidateQueries({
    queryKey: [...keys.booking.all, "domestic"],
  });
}

export function useCreateBookingDomestic(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createBookingDomestic,
    onSuccess: () => invalidateBookingDomestic(qc),
    ...options,
  });
}

export function useUpdateBookingDomestic(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateBookingDomestic,
    onSuccess: () => invalidateBookingDomestic(qc),
    ...options,
  });
}

export function useDeleteBookingDomestic(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteBookingDomestic,
    onSuccess: () => invalidateBookingDomestic(qc),
    ...options,
  });
}

/* ============================================================
 * Booking International
 * ============================================================ */

export function useBookingInternational(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.booking.international(p(params))),
    queryFn: () => fetchBookingInternational(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useBookingInternationalDetail(id, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.booking.all, "international", "detail", p(id)]),
    queryFn: () => fetchBookingInternationalById(p(id)),
    staleTime: DETAIL_STALE,
    enabled: computed(() => !!p(id)),
    ...options,
  });
}

function invalidateBookingInternational(qc) {
  return qc.invalidateQueries({
    queryKey: [...keys.booking.all, "international"],
  });
}

export function useCreateBookingInternational(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createBookingInternational,
    onSuccess: () => invalidateBookingInternational(qc),
    ...options,
  });
}

export function useUpdateBookingInternational(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateBookingInternational,
    onSuccess: () => invalidateBookingInternational(qc),
    ...options,
  });
}

export function useDeleteBookingInternational(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteBookingInternational,
    onSuccess: () => invalidateBookingInternational(qc),
    ...options,
  });
}

/* ============================================================
 * Booking Kalogistics
 * ============================================================ */

export function useBookingKalogistics(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.booking.kalogistics(p(params))),
    queryFn: () => fetchBookingKalogistics(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useKalogisticsOrigins(options = {}) {
  return useQuery({
    queryKey: [...keys.booking.all, "kalogistics", "origins"],
    queryFn: fetchBookingKalogisticsOrigins,
    staleTime: REF_STALE,
    ...options,
  });
}

export function useKalogisticsDestinations(originId, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.booking.all,
      "kalogistics",
      "destinations",
      p(originId),
    ]),
    queryFn: () => fetchBookingKalogisticsDestinations(p(originId)),
    staleTime: REF_STALE,
    enabled: computed(() => !!p(originId)),
    ...options,
  });
}

export function useApproveBookingKalogistics(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: approveBookingKalogistics,
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...keys.booking.all, "kalogistics"],
      }),
    ...options,
  });
}
